import type Feature from 'ol/Feature'
import type Geometry from 'ol/geom/Geometry'

import { mapAtom } from '../../atoms/map/map'
import { createNonBlockingRenderByTime } from '../../renderingQueue'
import { setLayerMetadata } from '../layerMetadata'
import { findLayer } from '../utils/findLayer'

import { createBreadcrumbPathFeature } from './utils/breadcrumbAndRoutePath/createBreadcrumbPathFeature'
import { createRoutePolylineFeature } from './utils/breadcrumbAndRoutePath/createRoutePolylineFeature'
import { createBreadcrumbPointsFeatures } from './utils/breadcrumbPoints/createBreadcrumbPointsFeatures'
import { createBreadcrumbStopsFeatures } from './utils/breadcrumbStops/createBreadcrumbStopsFeatures'

const nonblockingRender = createNonBlockingRenderByTime()

export const initializeBreadcrumbAndRoutesFeatures = async (
  routeMarkers: uui.domain.ui.map.markers.MapMarkers['routePolyline'],
  routeMapStyles: uui.domain.ui.map.markers.MapStyles['routePolyline'],
  breadcrumbMarkers: uui.domain.ui.map.markers.MapMarkers['breadcrumb'],
  breadcrumbMapStyles: uui.domain.ui.map.markers.MapStyles['breadcrumb'],
  selection: Set<string>,
  breadcrumbTimeRange: uui.domain.BreadcrumbTimeRange,
) => {
  const map = mapAtom.map
  const layersIds = ['breadcrumbPoints', 'breadcrumbPathAndRoutePolyline'] as const

  for (const layerId of layersIds) {
    // Breadcrumb layer
    const layer = findLayer(map, layerId)

    const features: Feature<Geometry>[] = []

    nonblockingRender.reset()

    for (const marker of Object.values(routeMarkers)) {
      await nonblockingRender.next()

      if (layerId === 'breadcrumbPathAndRoutePolyline') {
        features.push(createRoutePolylineFeature(marker, routeMapStyles))
      }
    }

    for (const marker of Object.values(breadcrumbMarkers)) {
      await nonblockingRender.next()

      const stopFeatures = createBreadcrumbStopsFeatures(
        marker,
        breadcrumbMapStyles,
        selection,
        breadcrumbTimeRange,
      )

      switch (layerId) {
        case 'breadcrumbPoints':
          features.push(
            ...createBreadcrumbPointsFeatures(
              marker,
              breadcrumbMapStyles,
              selection,
              breadcrumbTimeRange,
            ),
          )
          features.push(...stopFeatures)
          break

        case 'breadcrumbPathAndRoutePolyline':
          features.push(createBreadcrumbPathFeature(marker, breadcrumbMapStyles))
          features.push(...stopFeatures)
          break
      }
    }

    // add all new features to the layer
    layer.getSource()?.addFeatures(features)

    // keep track of selected items in the current layer
    setLayerMetadata(layer, 'selection', new Set(selection))
  }
}
